import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MdsModalService } from '@meddev/fe-shared';
import { BookingConfirmationService, SelfBookingManagerService, TimeSlotBookingModel } from '@shared/booking-feature';
import { APP_ROUTE_CONSTANTS, DEFAULT_TOAST_SETTINGS } from '@shared/core';
import { Dayjs } from '@shared/utils';
import { catchError, filter, Observable, of, switchMap, tap } from 'rxjs';
import { CustomerClaimsAppService } from '../../../../app-layers';

@Injectable({
    providedIn: 'root',
})
export class ModalBookingConfirmationService extends BookingConfirmationService {
    private readonly mdsModalService = inject(MdsModalService);
    private readonly reportClaimAppService = inject(CustomerClaimsAppService);
    private readonly selfBookingManager = inject(SelfBookingManagerService);
    private readonly router = inject(Router);
    private readonly appRouteConstants = inject(APP_ROUTE_CONSTANTS);

    confirmBooking(slot: TimeSlotBookingModel): Observable<unknown> {
        const confirmTitle = $localize`:@@confirmAppointmentTitle:Confirm appointment`;
        const slotStartDateTime = (slot.start as Dayjs).format('DD.MM.YYYY HH:mm');
        const slotEndTime = (slot.end as Dayjs).format('HH:mm');
        const content =
            $localize`:@@confirmAppointment:Do you wish to confirm selected appointment?` +
            `<br><br>` +
            `${slotStartDateTime} - ${slotEndTime}` +
            `<br>` +
            `${slot.subcontractorName}`;
        const modalSettings = {
            showConfirmButton: true,
            showDenyButton: false,
            showCancelButton: true,
            customClass: {
                confirmButton: 'btn btn-success text-white text-uppercase',
                cancelButton: 'btn btn-secondary text-uppercase',
            },
            confirmButtonText: $localize`Book appointment`,
        };

        return this.mdsModalService.showModal(confirmTitle, content, 'question', modalSettings).pipe(
            filter(result => result.isConfirmed),
            switchMap(() => {
                return this.selfBookingManager.createPrereservation(slot.guid).pipe(
                    tap(() => {
                        this.reportClaimAppService.refreshCustomerReportClaims();
                        this.mdsModalService.showSuccessModal($localize`Booking successful`, '', DEFAULT_TOAST_SETTINGS);
                        this.router.navigate([this.appRouteConstants['claims']]);
                    }),
                    catchError(() => {
                        this.mdsModalService.showErrorModal(
                            $localize`Error while booking an appointment.`,
                            $localize`Please try again later.`,
                            DEFAULT_TOAST_SETTINGS,
                        );
                        return of(null);
                    }),
                );
            }),
        );
    }
}
